import cn from 'classnames';
import type { ReactNode } from 'react';

interface FieldsetProps {
  legend: string;
  description?: string;
  children: ReactNode;
}

export function Fieldset({ legend, description, children }: FieldsetProps) {
  return (
    <fieldset className="mb-8 last:mb-0">
      <legend className="mb-1 text-base font-semibold text-heading">
        {legend}
      </legend>
      {description ? (
        <p className="mb-4 text-sm text-body">{description}</p>
      ) : (
        <div className="mb-4" />
      )}
      {children}
    </fieldset>
  );
}

interface ChoiceGroupProps {
  label: string;
  name: string;
  options: Array<{ value: string; label: string; hint?: string }>;
  value?: string;
  onChange: (value: string) => void;
  error?: string;
  columns?: 1 | 2;
}

/** Radio group rendered as tappable cards — easier targets than native radios on mobile. */
export function ChoiceGroup({
  label,
  name,
  options,
  value,
  onChange,
  error,
  columns = 2,
}: ChoiceGroupProps) {
  return (
    <div
      role="radiogroup"
      aria-labelledby={`${name}-label`}
      aria-invalid={error ? 'true' : 'false'}
      aria-describedby={error ? `${name}-error` : undefined}
    >
      <span
        id={`${name}-label`}
        className="mb-3 block text-sm font-semibold leading-none text-gray-200"
      >
        {label}
      </span>
      <div
        className={cn(
          'grid gap-3',
          columns === 2 ? 'sm:grid-cols-2' : 'grid-cols-1',
        )}
      >
        {options.map((option) => {
          const id = `${name}-${option.value}`;
          const checked = value === option.value;

          return (
            <label
              key={option.value}
              htmlFor={id}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition',
                checked
                  ? 'border-accent bg-accent/5'
                  : 'border-border-base hover:border-accent/50',
              )}
            >
              <input
                id={id}
                type="radio"
                name={name}
                value={option.value}
                checked={checked}
                onChange={() => onChange(option.value)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-heading">
                  {option.label}
                </span>
                {option.hint && (
                  <span className="mt-0.5 block whitespace-pre-line text-xs text-body">
                    {option.hint}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>
      {error && (
        <p id={`${name}-error`} className="mt-2 text-xs text-red-500">
          {error}
        </p>
      )}
    </div>
  );
}

interface CheckboxGroupProps {
  label: string;
  name: string;
  options: string[];
  values: string[];
  onChange: (values: string[]) => void;
  error?: string;
}

export function CheckboxGroup({
  label,
  name,
  options,
  values,
  onChange,
  error,
}: CheckboxGroupProps) {
  const toggle = (option: string) => {
    onChange(
      values.includes(option)
        ? values.filter((item) => item !== option)
        : [...values, option],
    );
  };

  return (
    <div
      role="group"
      aria-labelledby={`${name}-label`}
      aria-describedby={error ? `${name}-error` : undefined}
    >
      <span
        id={`${name}-label`}
        className="mb-3 block text-sm font-semibold leading-none text-gray-200"
      >
        {label}
      </span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const id = `${name}-${option.replace(/\s+/g, '-').toLowerCase()}`;
          const checked = values.includes(option);

          return (
            <label
              key={option}
              htmlFor={id}
              className={cn(
                'flex cursor-pointer items-center gap-2 rounded-full border px-4 py-2 text-sm transition',
                checked
                  ? 'border-accent bg-accent/10 text-accent'
                  : 'border-border-base text-body hover:border-accent/50',
              )}
            >
              <input
                id={id}
                type="checkbox"
                checked={checked}
                onChange={() => toggle(option)}
                className="h-4 w-4 accent-accent"
              />
              {option}
            </label>
          );
        })}
      </div>
      {error && (
        <p id={`${name}-error`} className="mt-2 text-xs text-red-500">
          {error}
        </p>
      )}
    </div>
  );
}

interface ConsentCheckboxProps {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  error?: string;
  children: ReactNode;
}

export function ConsentCheckbox({
  id,
  checked,
  onChange,
  error,
  children,
}: ConsentCheckboxProps) {
  return (
    <div>
      <label htmlFor={id} className="flex cursor-pointer items-start gap-3">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${id}-error` : undefined}
          className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
        />
        <span className="text-sm leading-relaxed text-body">{children}</span>
      </label>
      {error && (
        <p id={`${id}-error`} className="mt-2 text-xs text-red-500">
          {error}
        </p>
      )}
    </div>
  );
}
